import {
  ColumnDef,
  DisplayColumnDef,
  InitialTableState,
  RowData,
} from "@tanstack/react-table";
import {
  forwardRef,
  ReactElement,
  ReactNode,
  Ref,
  Suspense,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { IconButtonV2 } from "../../primitives";
import { InputV2 } from "../../primitives/input";
import { ProgressSpinner } from "../loader";
import { PageSection, SectionHeader, SectionId } from "../section";
import type { Table as TableType } from "../table";
import Table from "../table";
import styles from "./related-list.module.css";

type RelatedListItem = RowData;

export type RelatedListProps<D extends RelatedListItem> = {
  title: string;
  counterClassName?: string;
  id: SectionId;
  items: D[] | undefined;
  actions?: ReactNode;
  columns: Array<ColumnDef<D, any>>;
  initialState?: InitialTableState;
  error?: unknown;
  status?: "error" | "success" | "loading" | "refetching" | "pending";
  refreshData?: () => void;
  searchPlaceholder?: string;
  // Page Nav
  onViewLeave: (sectionName: string) => void;
  onViewEnter: (sectionName: string) => void;

  onSectionRegister: (sectionName: string) => void;
  onSectionUnregister: (sectionName: string) => void;
};

export type RelatedListHandler<D extends RelatedListItem> = {
  setData(data: D[]): void;
};

export const RelatedListSearch = Symbol("RELATED_LIST_SEARCH");

// ToDo: Fix hardcoded generic types
export const RelatedList = forwardRef<
  RelatedListHandler<RowData>,
  RelatedListProps<RowData>
>(
  (
    {
      actions,
      items,
      title,
      counterClassName,
      id,
      columns,
      initialState,
      status,
      refreshData,
      searchPlaceholder = "Search...",
      onViewEnter,
      onViewLeave,
      onSectionRegister,
      onSectionUnregister,
      error,
    }: RelatedListProps<RowData>,
    ref,
  ) => {
    const [search, setSearch] = useState<string>("");

    const tableRef = useRef<TableType<RowData>>(null);

    useEffect(
      () =>
        tableRef.current?.setGlobalFilter?.(
          (filter: Record<string, unknown>) => ({
            ...filter,
            [RelatedListSearch]: search,
          }),
        ),
      [search],
    );

    const setData = (d: RowData[]) => {
      tableRef.current?.setData(d);
    };

    useImperativeHandle(ref, () => ({
      setData,
    }));

    const itemsLength = items?.length ?? 0;

    const paddingLeftColumn: DisplayColumnDef<unknown> = {
      id: "padding_left",
      meta: {
        gridSize: "20px",
      },
      size: 20,
    };

    const paddingRightColumn: DisplayColumnDef<unknown> = {
      id: "padding_right",
      meta: {
        gridSize: "20px",
      },
      size: 20,
    };

    return (
      <PageSection
        id={id}
        className={styles.related_list}
        onViewLeave={onViewLeave}
        onViewEnter={onViewEnter}
        onSectionRegister={onSectionRegister}
        onSectionUnregister={onSectionUnregister}
      >
        <SectionHeader
          className={styles.title}
          title={title}
          counterClassName={counterClassName}
          counter={
            status === "loading" ||
            status === "refetching" ||
            status === "pending"
              ? "loading"
              : status === "error"
                ? "error"
                : itemsLength
          }
          actions={
            <>
              <div className="flex-1" />
              {itemsLength > 0 && (
                <InputV2
                  value={search}
                  size="sm"
                  iconLeft={"Search"}
                  containerClassName={styles.search}
                  onChange={(v) => setSearch(v!)}
                  placeholder={searchPlaceholder}
                />
              )}
              {refreshData && (
                <IconButtonV2
                  variant="outline"
                  className="flex-shrink-0"
                  icon={"RefreshCw"}
                  onClick={() => refreshData()}
                />
              )}
              {actions}
            </>
          }
        />
        {items !== undefined &&
          status !== "loading" &&
          status !== "pending" && (
            <Suspense fallback={<ProgressSpinner />}>
              {itemsLength > 0 && (
                <Table
                  ref={tableRef}
                  columns={[paddingLeftColumn, ...columns, paddingRightColumn]}
                  defaultData={items}
                  pagination={{ enabled: true }}
                  initialState={initialState}
                >
                  No {title} found
                </Table>
              )}
            </Suspense>
          )}
      </PageSection>
    );
  },
) as <D extends RelatedListItem>(
  p: RelatedListProps<D> & { ref?: Ref<RelatedListHandler<D>> },
) => ReactElement;

export default RelatedList;
